import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, ShieldCheck, AlertTriangle } from "lucide-react";
import { getApiUrl as resolveApiUrl } from "@/lib/runtimeConfig";

const API = resolveApiUrl();

export default function AcceptInvitePage() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();

    const [preview, setPreview] = useState(null);  // {email, name, role, expires_at}
    const [error, setError] = useState(null);
    const [pwd, setPwd] = useState("");
    const [pwd2, setPwd2] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!token) { setError("This invite link is missing its token."); return; }
        let alive = true;
        axios.get(`${API}/auth/invite/preview?token=${encodeURIComponent(token)}`)
            .then((r) => { if (alive) setPreview(r.data); })
            .catch((e) => {
                if (!alive) return;
                setError(e?.response?.data?.detail || "This invite link is no longer valid.");
            });
        return () => { alive = false; };
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        if (pwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (pwd !== pwd2) { toast.error("Passwords don't match"); return; }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/auth/accept-invite`, { token, password: pwd });
            localStorage.setItem("admin_token", data.token);
            localStorage.setItem("admin_user", JSON.stringify(data.user));
            toast.success("Welcome — your account is active");
            navigate("/", { replace: true });
        } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Could not accept the invite.");
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center px-4 py-10" data-testid="accept-invite-page">
            <div className="w-full max-w-md bg-[#0c0c10] border border-white/10 rounded-2xl p-8">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center font-display text-white text-lg font-extrabold tracking-tight"
                        style={{ background: "linear-gradient(135deg,#a17bff 0%,#7C3AED 55%,#4c1d95 100%)" }}>
                        Ai
                    </div>
                    <div>
                        <div className="font-display text-lg tracking-tight">AIService</div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40">Accept invite</div>
                    </div>
                </div>

                {error ? (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4 flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" strokeWidth={1.6} />
                        <div>
                            <div className="text-sm text-red-300">{error}</div>
                            <button
                                onClick={() => navigate("/login")}
                                data-testid="back-to-login"
                                className="mt-3 text-xs text-[#a17bff] hover:text-[#c7a6ff]"
                            >
                                ← Back to sign in
                            </button>
                        </div>
                    </div>
                ) : !preview ? (
                    <div className="text-sm text-white/50 py-12 text-center">Validating invite…</div>
                ) : (
                    <>
                        <div className="bg-[#7C3AED]/10 border border-[#7C3AED]/30 rounded-md p-4 mb-5">
                            <div className="flex items-center gap-2 text-xs text-[#a17bff] mb-1">
                                <ShieldCheck className="w-3.5 h-3.5" />
                                <span className="uppercase tracking-wider">Invite valid</span>
                            </div>
                            <div className="text-sm">
                                Hi <strong>{preview.name || preview.email}</strong>,
                            </div>
                            <div className="text-xs text-white/60 mt-1">
                                You've been invited to AIService as a{" "}
                                <strong className="text-white">{(preview.role || "admin").replace(/_/g, " ")}</strong>.
                                Set your password below to activate the account.
                            </div>
                        </div>

                        <form onSubmit={submit} className="space-y-3" data-testid="accept-invite-form">
                            <label className="block">
                                <div className="text-xs text-white/50 mb-1.5">New password</div>
                                <div className="relative">
                                    <input
                                        type={show ? "text" : "password"}
                                        value={pwd}
                                        onChange={(e) => setPwd(e.target.value)}
                                        autoFocus
                                        required minLength={8} maxLength={200}
                                        data-testid="invite-password"
                                        className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 pr-10 text-sm font-mono"
                                        placeholder="≥ 8 characters"
                                    />
                                    <button type="button" onClick={() => setShow((s) => !s)}
                                        className="absolute right-2 top-1.5 text-white/40 hover:text-white/70 p-1"
                                        aria-label={show ? "hide password" : "show password"}>
                                        {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                    </button>
                                </div>
                            </label>
                            <label className="block">
                                <div className="text-xs text-white/50 mb-1.5">Confirm password</div>
                                <input
                                    type={show ? "text" : "password"}
                                    value={pwd2}
                                    onChange={(e) => setPwd2(e.target.value)}
                                    required minLength={8} maxLength={200}
                                    data-testid="invite-password-confirm"
                                    className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                                />
                            </label>
                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="accept-invite-submit"
                                className="w-full mt-3 px-4 py-2.5 rounded-md bg-[#7C3AED] hover:bg-[#6928d6] disabled:opacity-50 text-white text-sm font-medium"
                            >
                                {busy ? "Activating…" : "Activate account & sign in"}
                            </button>
                        </form>

                        <div className="text-[10px] font-mono text-white/30 mt-4 text-center">
                            Link expires {(preview.expires_at || "").replace("T", " ").slice(0, 19)} UTC
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
